import React, { useCallback, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import StripePortalSessionQuery from './StripePortalSessionQuery';

export type GetStripePortalSessionUrlFn = (
  subscriptionId: number,
  returnUrl: string
) => Promise<string | null>;

export interface StripePortalSessionResult {
  /** Resolves to the Stripe Billing Portal URL, or null when the backend refused the session. */
  getPortalSessionUrl: GetStripePortalSessionUrlFn;
  loading: boolean;
}

interface Props {
  children: (result: StripePortalSessionResult) => React.ReactElement;
}

interface QueryData {
  sale: {
    stripe: {
      portalSession: { url: string } | null;
    } | null;
  } | null;
}

const StripePortalSessionConnectorApollo: React.FC<Props> = ({ children }) => {
  const client = useApolloClient();
  const [loading, setLoading] = useState(false);

  const getPortalSessionUrl = useCallback<GetStripePortalSessionUrlFn>(
    async (subscriptionId, returnUrl) => {
      setLoading(true);
      try {
        const { data } = await client.query<QueryData>({
          query: StripePortalSessionQuery,
          variables: { subscriptionId, returnUrl },
          // A portal session is single-use and short-lived, never serve it from cache.
          fetchPolicy: 'network-only',
        });
        return data?.sale?.stripe?.portalSession?.url ?? null;
      } catch {
        return null;
      } finally {
        setLoading(false);
      }
    },
    [client]
  );

  return children({ getPortalSessionUrl, loading });
};

export default StripePortalSessionConnectorApollo;
